import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import { DEFAULT_PAGINATION } from 'commons/constants';
import produce from 'immer';
import { storeName as entityStore } from 'store/entities/tenants.entity';
import { PageRequest } from 'types/common.d';
import { TenantType } from 'types/entities/tenants.d';
import { TenantsModel } from 'types/models/tenants.d';
import { PaginationType } from 'types/response.d';
import { RootModel } from '../models';

export const storeName = 'tenantsModel';

const initialState: TenantsModel = {
  ids: [],
  pagination: DEFAULT_PAGINATION,
  editId: null,
  loading: false,
  criteria: {
    keyword: '',
  },
};

export const tenantsModel = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
    updateCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    updateLoading: produce((state, payload) => {
      state.loading = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<typeof tenantsModel>) => ({
    async setEditItem(payload) {
      this.setEditId(payload);
    },
    async reloadList(payload, state) {
      const { criteria, pagination } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria });
    },
    async filterAsync(payload) {
      this.updateLoading(true);
      try {
        const data: { content: TenantType[] } & PaginationType = await dispatch[entityStore].load(
          payload
        );
        if (data && data.content) {
          this.setIds(data.content.map((scene) => scene.id));
          this.setPagination((({ content, ...otherProps }) => otherProps)(data));
        }
        this.updateCriteria(payload);
      } catch (e) {
        console.error(e);
        message.error('Failed to filter');
      } finally {
        this.updateLoading(false);
      }
    },
    async pagingAsync(payload: PageRequest, state) {
      const { criteria = {}, pagination = {} } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria, ...payload });
    },
    async getAsync(payload, state) {
      this.updateLoading(true);
      try {
        const item = await dispatch[entityStore].findById(payload);
        this.setEditId(item.id);
        this.updateLoading(false);
      } catch (e) {
        console.error(e);
        message.error(`Failed to get ${payload}`);
      } finally {
        this.updateLoading(false);
      }
    },
    async saveAsync(payload, state): Promise<void> {
      this.updateLoading(true);
      try {
        await dispatch[entityStore].save(payload);
        this.reloadList();
        message.success('Save successfully');
      } catch (err) {
        console.error(err);
        message.error('Fail to save');
        throw err;
      } finally {
        this.updateLoading(false);
      }
    },
  }),
});

export default tenantsModel;
